'use client';

import { useEffect } from 'react';

/**
 * TEMPORARY debug instrumentation (tickets #298/#303 — recurring "/recipes
 * infinite loading skeleton"). React 19 hydration mismatches (e.g. error
 * #419) don't always reach a route's error.tsx boundary — they can surface
 * as a plain `console.error` from React's internals with no boundary catch.
 * This listens at the window level so we get a wall-clock-timestamped record
 * of those regardless, to cross-reference against `[debug-timing]` server
 * logs for the same request. Grep `debug-timing` to find every related
 * instrumentation point and rip them out once root-caused.
 */

// Confirmed via a de-minified production stack trace (2026-07-01) that this
// crash happens entirely inside React's Suspense reconciler
// (updateSuspenseComponent in react-dom-client.production.js), invoked
// through Next.js's onRecoverableError hook — i.e. React itself decided the
// page's Suspense boundary couldn't be reconciled and gave up, with no app
// code anywhere in the stack. This matches a long-standing, still-open
// Next.js App Router bug (vercel/next.js#51581): a dynamic route reading
// searchParams combined with a Suspense boundary can throw React error #419
// in production with no clean recovery path. We can't patch React/Next.js
// from here, so this is a mitigation, not a fix: detect the specific
// unrecoverable-hydration error family and force exactly one reload. Guarded
// with sessionStorage so a genuinely deterministic recurrence (the bug fires
// again immediately after reload) does NOT loop — it just leaves the user on
// the same stuck page they'd have seen anyway, once, instead of reloading
// forever.
const HYDRATION_ERROR_CODES = [418, 419, 421, 422, 423];
const RELOAD_GUARD_KEY = 'cheffo-hydration-recovery-attempted';

function isUnrecoverableHydrationError(value: unknown): boolean {
  const message = value instanceof Error ? value.message : String(value ?? '');
  return HYDRATION_ERROR_CODES.some((code) => message.includes(`React error #${code}`));
}

function attemptRecovery(source: string) {
  console.error(`[debug-timing] unrecoverable hydration error detected via ${source} — checking reload guard`); // TEMPORARY
  let alreadyAttempted = false;
  try {
    alreadyAttempted = sessionStorage.getItem(RELOAD_GUARD_KEY) === window.location.pathname;
  } catch {
    // sessionStorage unavailable (private mode edge cases) — fail safe, don't reload
    return;
  }
  if (alreadyAttempted) {
    console.error('[debug-timing] reload already attempted for this path this session — not looping'); // TEMPORARY
    return;
  }
  try {
    sessionStorage.setItem(RELOAD_GUARD_KEY, window.location.pathname);
  } catch {
    return;
  }
  console.error('[debug-timing] reloading once to recover'); // TEMPORARY
  window.location.reload();
}

export function DebugErrorListener() {
  useEffect(() => {
    // If we're still here 5s after mount with no crash, this load succeeded —
    // clear the guard so a later, genuinely-flaky occurrence on this same
    // path still gets its one recovery attempt instead of being permanently
    // exhausted for the rest of the browser session.
    const clearGuardTimer = window.setTimeout(() => {
      try {
        if (sessionStorage.getItem(RELOAD_GUARD_KEY) === window.location.pathname) {
          sessionStorage.removeItem(RELOAD_GUARD_KEY);
        }
      } catch {
        // ignore
      }
    }, 5000);

    const onError = (event: ErrorEvent) => {
      console.error(
        `[debug-timing] window error at ${new Date().toISOString()} path=${window.location.pathname}`,
        event.error ?? event.message,
      );
      if (isUnrecoverableHydrationError(event.error ?? event.message)) {
        attemptRecovery('window error event');
      }
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      console.error(
        `[debug-timing] unhandledrejection at ${new Date().toISOString()} path=${window.location.pathname}`,
        event.reason,
      );
      if (isUnrecoverableHydrationError(event.reason)) {
        attemptRecovery('unhandledrejection event');
      }
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.clearTimeout(clearGuardTimer);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  return null;
}
